/**
 * The one place a client message is sent (frontend.md §1.3.2).
 *
 * Typed against `ClientMessageMap` in `@ordago/shared`, so a rename on either
 * end is a compile error rather than a message that silently goes nowhere.
 *
 * Optimistic UI is deliberately absent. The only local prediction anywhere in
 * the client is legality — `game.legal` and `@ordago/rules` grey buttons out —
 * and even that is advisory: an enabled-but-illegal button is merely rejected,
 * while a disabled-but-legal one is a bug. The server is always the truth.
 */

import type { Room } from 'colyseus.js';

import type { CardId, MusAction, Seat } from '@ordago/rules';
import type {
  ClientMessageMap,
  QuickPhraseId,
  ReportReason,
  RoomConfigPatch,
  SenaCode,
} from '@ordago/shared';

import { useStore } from '../store';

let current: Room<unknown> | null = null;

export function setActiveRoom(room: Room<unknown> | null): void {
  current = room;
}

export function activeRoom(): Room<unknown> | null {
  return current;
}

function post<K extends keyof ClientMessageMap>(name: K, payload: ClientMessageMap[K]): void {
  // A click that lands in the gap between `leave()` and unmount is normal, not
  // exceptional: drop it rather than throwing inside an event handler.
  current?.send(name, payload);
}

export const send = {
  ready: (ready: boolean) => {
    post('room.ready', { ready });
  },
  config: (patch: RoomConfigPatch) => {
    post('room.config', patch);
  },
  sit: (seat: Seat) => {
    post('room.seat', { seat });
  },
  addBot: (seat: Seat) => {
    post('room.addBot', { seat, kind: 'fill' });
  },
  removeBot: (seat: Seat) => {
    post('room.removeBot', { seat });
  },
  kick: (seat: Seat) => {
    post('room.kick', { seat });
  },
  start: () => {
    post('room.start', {});
  },

  /**
   * Ask a friend to this table. It is a room message rather than a `SocialRoom`
   * one because only the room knows whether a seat is free and whether the
   * match has started; the refusals arrive as ordinary `room.error` codes.
   */
  invite: (userId: string) => {
    post('room.invite', { userId });
  },

  /**
   * Take the seat back from the timeout autopilot (playtest 2, item 7). The
   * substitute answers every turn well inside a human reaction, so "just play
   * again" was not a way out; this is.
   */
  resume: () => {
    post('room.resume', {});
  },

  /**
   * The single gameplay channel; the server re-derives the seat and ignores
   * ours.
   *
   * The legal set is cleared on send. Not optimism about the *outcome* — the
   * server still arbitrates — but about having spoken: `game.legal` is pushed to
   * the acting seat, so there is no message that says "you are no longer up",
   * and leaving the old set in place offers a second click on a turn that has
   * already passed. The next `game.legal` repopulates it.
   */
  action: (action: MusAction) => {
    useStore.setState((s) => ({ table: { ...s.table, legal: [], legalSeat: null } }));
    post('game.action', { action });
  },

  sena: (sena: SenaCode) => {
    post('sena.send', { sena });
  },
  chat: (text: string) => {
    post('chat.send', { text });
  },
  quick: (phraseId: QuickPhraseId) => {
    post('chat.quick', { phraseId });
  },
  report: (seat: Seat, reason: ReportReason, message?: string) => {
    post('report.player', { seat, reason, ...(message === undefined ? {} : { message }) });
  },
};

/**
 * `MusAction` carries a seat the server ignores, but the type demands one. These
 * helpers keep every call site from repeating that awkwardness.
 */
export function actions(seat: Seat) {
  return {
    mus: (): MusAction => ({ type: 'MUS', seat }),
    noMus: (): MusAction => ({ type: 'NO_MUS', seat }),
    discard: (cards: CardId[]): MusAction => ({ type: 'DISCARD', seat, cards }),
    declare: (lance: 'pares' | 'juego'): MusAction => ({ type: 'DECLARE', seat, lance }),
    paso: (): MusAction => ({ type: 'PASO', seat }),
    envido: (amount: number): MusAction => ({ type: 'ENVIDO', seat, amount }),
    ordago: (): MusAction => ({ type: 'ORDAGO', seat }),
    quiero: (): MusAction => ({ type: 'QUIERO', seat }),
    noQuiero: (): MusAction => ({ type: 'NO_QUIERO', seat }),
  };
}
